import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { securityApi } from '../services/securityApi';
import type { CreateIntelSourceBody, ParserInfo } from '../types/security';

interface AddSourceModalProps {
  onClose: () => void;
  onCreated: () => void;
}

// WQ-285 (DEF-024 sibling): the parser list is now fetched live from
// GET /api/admin/security/parsers (source of truth = backend PARSER_REGISTRY).
// `FALLBACK_PARSER_TYPES` is used only if the endpoint errors — operator can
// still create a source against a known-good parser slug. Slugs match the
// registry (hyphen-separated for compound names).
const FALLBACK_PARSER_TYPES: ReadonlyArray<ParserInfo> = [
  { slug: 'aws-alas', class_name: 'AWSALASSource', display_name: 'AWS Amazon Linux Security Advisories' },
  { slug: 'cisa-kev', class_name: 'CISAKEVSource', display_name: 'CISA Known Exploited Vulnerabilities' },
  { slug: 'ghsa', class_name: 'GHSASource', display_name: 'GitHub Security Advisories' },
  { slug: 'nvd', class_name: 'NVDSource', display_name: 'National Vulnerability Database' },
  { slug: 'osv', class_name: 'OSVSource', display_name: 'Open Source Vulnerabilities' },
];

const SOURCE_CATEGORIES: CreateIntelSourceBody['source_category'][] = [
  'security',
  'framework_release',
  'ecosystem',
  'compliance',
];

const SCHEDULE_INTERVALS = ['hourly', 'daily', 'weekly'];

export function AddSourceModal({ onClose, onCreated }: AddSourceModalProps): React.JSX.Element {
  const { authFetch } = useAuth();
  const [name, setName] = useState('');
  const [category, setCategory] = useState<CreateIntelSourceBody['source_category']>('security');
  const [url, setUrl] = useState('');
  const [parsers, setParsers] = useState<ReadonlyArray<ParserInfo>>(FALLBACK_PARSER_TYPES);
  const [parsersLoading, setParsersLoading] = useState(true);
  const [parsersFallback, setParsersFallback] = useState(false);
  const [sourceType, setSourceType] = useState<string>(FALLBACK_PARSER_TYPES[0].slug);
  const [scheduleInterval, setScheduleInterval] = useState('daily');
  const [configText, setConfigText] = useState('{}');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await securityApi.listParsers(authFetch);
        if (cancelled) return;
        if (resp.parsers && resp.parsers.length > 0) {
          setParsers(resp.parsers);
          setSourceType(resp.parsers[0].slug);
          setParsersFallback(false);
        } else {
          // Empty array treated as error per guidance §4 step 6
          setParsersFallback(true);
        }
      } catch {
        if (!cancelled) setParsersFallback(true);
      } finally {
        if (!cancelled) setParsersLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [authFetch]);

  const valid =
    name.trim().length > 0 &&
    url.trim().length > 0 &&
    /^https?:\/\//i.test(url.trim()) &&
    sourceType.trim().length > 0 &&
    scheduleInterval.trim().length > 0;

  const handleSubmit = async () => {
    setError(null);
    let config: Record<string, unknown> = {};
    if (configText.trim()) {
      try {
        config = JSON.parse(configText);
        if (typeof config !== 'object' || config === null || Array.isArray(config)) {
          throw new Error('config must be a JSON object');
        }
      } catch (e) {
        setError(`Invalid JSON in config: ${e instanceof Error ? e.message : String(e)}`);
        return;
      }
    }
    setBusy(true);
    try {
      await securityApi.createIntelSource(authFetch, {
        name: name.trim(),
        source_category: category,
        source_url: url.trim(),
        source_type: sourceType.trim(),
        schedule_interval: scheduleInterval.trim(),
        config,
      });
      onCreated();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  // Portal to document.body to escape `.admin-page-content` stacking context
  // (position: relative; z-index: 1 in admin-portal.css). Without portaling,
  // no z-index value can stack above `.admin-header` (z-index: 1000) because
  // the header is in the parent stacking context. Pattern matches
  // NodeDetailModal / FinancialDetailModal in components/adjacency-map/.
  const modalJsx = (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-source-title"
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1002,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          background: 'var(--bg-primary, white)',
          borderRadius: '0.5rem',
          padding: '1.25rem',
          width: 'min(520px, 90vw)',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
        }}
      >
        <h3 id="add-source-title" style={{ marginTop: 0 }}>Add intel source</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <strong>Name:</strong>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={255}
              placeholder="e.g., custom-cve-feed"
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <strong>Category:</strong>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value as CreateIntelSourceBody['source_category'])}
            >
              {SOURCE_CATEGORIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <strong>URL:</strong>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              maxLength={1024}
              placeholder="https://example.org/feed.json"
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <strong>Parser (source_type):</strong>
            <select
              value={sourceType}
              onChange={(e) => setSourceType(e.target.value)}
              disabled={parsersLoading}
              aria-busy={parsersLoading}
            >
              {parsers.map((p) => (
                <option key={p.slug} value={p.slug}>
                  {p.display_name} ({p.slug})
                </option>
              ))}
            </select>
            {parsersLoading && (
              <span
                role="status"
                aria-live="polite"
                style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}
              >
                Loading parser list…
              </span>
            )}
            {!parsersLoading && parsersFallback && (
              <span
                role="alert"
                style={{ fontSize: '0.75rem', color: 'var(--severity-high-text, #b45309)' }}
              >
                Using cached parser list — backend `/parsers` endpoint unreachable.
              </span>
            )}
            <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
              Must be a registered parser. New parsers ship via cyber-agent.
            </span>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <strong>Schedule:</strong>
            <select
              value={scheduleInterval}
              onChange={(e) => setScheduleInterval(e.target.value)}
            >
              {SCHEDULE_INTERVALS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <strong>Config (JSON):</strong>
            <textarea
              value={configText}
              onChange={(e) => setConfigText(e.target.value)}
              rows={4}
              style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
            />
          </label>
        </div>
        {error && (
          <div
            role="alert"
            style={{
              marginTop: '0.75rem',
              color: 'var(--severity-critical-text, #991b1b)',
              fontSize: '0.875rem',
            }}
          >
            {error}
          </div>
        )}
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={busy || !valid}
            aria-disabled={busy || !valid}
          >
            {busy ? 'Creating…' : 'Create source'}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalJsx, document.body);
}
